import React, {useState, useEffect, useRef} from 'react';
import {AgentInfo} from '@/common-pages/proxy/proxy.type';
import {goTo} from '@/utils';
import ProgressBar from './progress-bar';
import {useShare} from '@/common-pages/hooks/share.hooks';
import {Swiper, SwiperRef, SwiperSlide} from 'swiper/react';
import 'swiper/css';
import AspectRatio from '@/components/basic/aspect-ratio';
import {ReactComponent as TeamSizeIcon} from '@/pages/agent/assets/my-teams-person.svg';
import {ReactComponent as TeamRechargeIcon} from '@/pages/agent/assets/my-teams-coin.svg';
import styles from './user-data-section.module.scss';
import {useTranslation} from 'react-i18next';
import {useUserDataSection} from '../use-user-data-section';
import {ProgressiveCircularLoading} from '@fe-progressive/package-progressive/progressive-loading';

// 资源文件
const myTeamBgImage = require('@/pages/agent/assets/my-team-bg.webp');
const betRebateImage = require('@/pages/agent/assets/bet-rebate.webp');
const rechargeRebateImage = require('@/pages/agent/assets/recharge-rebate.webp');
const rebateIncreaseImage = require('@/pages/agent/assets/rebate-increase.webp');
const carouselLeftImage = require('@/pages/agent/assets/carousel-left.webp');

interface UserDataSectionProps {
  agentInfo?: AgentInfo;
}

interface TeamCardProps {
  level: number;
  levelConfig: any;
  cardData: {
    betRebate: string;
    rechargeRebate: string;
    teamSize: {
      current: number;
      target: number | string;
    };
    teamRecharge: {
      current: number;
      target: number | string;
    };
    reachedFlag: boolean;
  };
  userLevel: number;
  i18n: any;
}

// 单个团队卡片组件
const TeamCard: React.FC<TeamCardProps> = ({
  level,
  levelConfig,
  cardData,
  userLevel,
  i18n,
}) => {
  const isAchieved = cardData.reachedFlag || level < userLevel;

  return (
    <AspectRatio className="rounded-[0.75rem] w-full" ratio={351 / 277}>
      <div className="relative rounded-[0.75rem] size-full">
        <div className="overflow-clip relative rounded-[inherit] w-full h-full">
          {/* 整个卡片背景图 */}
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{backgroundImage: `url(${myTeamBgImage})`}}
          />

          <div className="absolute left-3 top-2 text-white text-xs">
            {i18n.t('agent.myTeam.title')}
          </div>

          {/* 内容区域 */}
          <div className="flex flex-col gap-3 items-start px-4 py-0 pt-[2.4375rem] relative h-full">
            {/* Team Level 行 */}
            <div className="flex items-center justify-between relative shrink-0 w-full">
              <div className="flex gap-2 items-center relative shrink-0">
                <p
                  className="capitalize font-medium text-sm text-center text-nowrap tracking-[-0.3px] whitespace-pre"
                  style={{color: levelConfig.textMedium}}>
                  {i18n.t('agent.myTeam.teamLevel')}
                </p>
                <div className="flex gap-2 items-center relative shrink-0">
                  <div className="h-5 relative shrink-0 w-[3.1875rem]">
                    <img
                      src={levelConfig.badgeImage}
                      alt={`Level ${level}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>
              <div
                className={styles.detailsButton}
                onClick={() => goTo('AgentDetail')}>
                <p className={styles.detailsButtonText}>
                  {i18n.t('agent.myTeam.details')}
                </p>
              </div>
            </div>

            {/* 进度条区域 */}
            <div className="flex flex-col gap-2 items-start relative shrink-0 w-full">
              {isAchieved ? (
                <div className="flex items-center justify-center w-full py-2">
                  <p className="text-sm text-[#9E5A17] font-medium text-center">
                    {i18n.t('agent.myTeam.alreadyAchieved')}
                  </p>
                </div>
              ) : (
                <>
                  <ProgressBar
                    icon={<TeamSizeIcon className="size-4" />}
                    label={i18n.t('agent.myTeam.teamSize')}
                    current={cardData.teamSize.current}
                    target={cardData.teamSize.target}
                    showDecimals={false}
                  />
                  <ProgressBar
                    icon={<TeamRechargeIcon className="size-4" />}
                    label={i18n.t('agent.myTeam.teamRecharge')}
                    current={cardData.teamRecharge.current}
                    target={cardData.teamRecharge.target}
                    showDecimals={true}
                  />
                </>
              )}
            </div>

            {/* My Rebate 区域 */}
            <div className="box-border flex flex-col items-center overflow-clip pb-3 pt-[0.3125rem] px-[2.8125rem] relative rounded-[inherit] w-full">
              {/* My Rebate 标题 */}
              <p className="font-semibold text-sm text-[#935518] text-nowrap whitespace-pre">
                {i18n.t('agent.myTeam.myRebate')}
              </p>

              {/* Rebate 卡片 */}
              <div className="flex items-center gap-11">
                {/* Bet Rebate 卡片 */}
                <div className="flex flex-col items-center w-[6.1875rem]">
                  <div className="overflow-hidden relative shrink-0 w-[3.5rem] h-[3.5rem]">
                    <div className="absolute left-[0.140625rem] size-[3.359375rem] top-[0.140625rem]">
                      <img
                        alt=""
                        className="absolute inset-0 max-w-none object-50%-50% object-cover pointer-events-none size-full"
                        src={betRebateImage}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center relative shrink-0 w-full">
                    <p className="font-medium text-xs text-center text-white w-full">
                      {i18n.t('agent.myTeam.betRebate')}
                    </p>
                    <div className="flex gap-[0.1875rem] items-center justify-center relative shrink-0 w-full">
                      <p className="font-medium text-xs text-nowrap text-white whitespace-pre">
                        <span>{i18n.t('agent.myTeam.upTo')} </span>
                        <span className="font-bold text-[#ffea00]">
                          {cardData.betRebate}
                        </span>
                      </p>
                      <div className="h-3 relative shrink-0 w-[0.902rem]">
                        <img
                          alt=""
                          className="absolute inset-0 max-w-none object-50%-50% object-cover pointer-events-none size-full"
                          src={rebateIncreaseImage}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recharge Rebate 卡片 */}
                <div className="flex flex-col items-center w-[6.1875rem]">
                  <div className="overflow-hidden relative shrink-0 w-[3.5rem] h-[3.5rem]">
                    <div className="absolute left-[-0.244rem] size-[3.989rem] top-[-0.244rem]">
                      <img
                        alt=""
                        className="absolute inset-0 max-w-none object-50%-50% object-cover pointer-events-none size-full"
                        src={rechargeRebateImage}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center relative shrink-0 w-full">
                    <p className="font-medium text-xs text-center text-white w-full">
                      {i18n.t('agent.myTeam.rechargeRebate')}
                    </p>
                    <div className="flex gap-[0.1875rem] items-center justify-center relative shrink-0 w-full">
                      <p className="font-medium text-xs text-nowrap text-white whitespace-pre">
                        <span>{i18n.t('agent.myTeam.upTo')} </span>
                        <span className="font-bold text-[#ffea00]">
                          {cardData.rechargeRebate}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 阴影效果 */}
        <div className="absolute inset-0 pointer-events-none shadow-[0px_2px_0px_0px_inset_rgba(255,255,255,0.1)]" />
      </div>
    </AspectRatio>
  );
};

const UserDataSection: React.FC<UserDataSectionProps> = ({agentInfo}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperRef>(null);
  const {doShare} = useShare(true);
  const {i18n} = useTranslation();

  // 使用自定义 hook 获取数据和逻辑
  const {loading, userLevel, teamCards} = useUserDataSection(agentInfo);

  // 等级自动定位：根据用户当前等级自动轮播到对应等级
  useEffect(() => {
    if (swiperRef.current && agentInfo?.agentLevel) {
      const targetIndex = Math.min(agentInfo.agentLevel - 1, 3); // 确保索引在0-3范围内
      swiperRef.current.swiper?.slideTo(targetIndex);
      setActiveIndex(targetIndex);
    }
  }, [agentInfo?.agentLevel]);

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.swiper?.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.swiper?.slideNext();
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {loading && (
        <div className="flex justify-center py-1">
          <ProgressiveCircularLoading size={20} />
        </div>
      )}
      {/* My Team Cards 轮播 */}
      <div className="relative">
        <Swiper
          ref={swiperRef}
          slidesPerView={1}
          spaceBetween={0}
          onSlideChange={(swiper: any) => setActiveIndex(swiper.activeIndex)}
          className="w-full">
          {teamCards.map(({level, levelConfig, cardData}) => (
            <SwiperSlide key={level}>
              <TeamCard
                level={level}
                levelConfig={levelConfig}
                cardData={cardData}
                userLevel={userLevel}
                i18n={i18n}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* 轮播控制 */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-3 flex gap-4 items-center justify-center z-[5]">
          <div
            className="relative shrink-0 size-4 cursor-pointer"
            onClick={handlePrev}>
            <img
              alt=""
              className="block max-w-none size-full"
              src={carouselLeftImage}
            />
          </div>

          {/* 指示器 */}
          <div className="flex gap-1 items-start p-1 relative rounded-4">
            {[0, 1, 2, 3].map(index => (
              <div
                key={index}
                className={`rounded-[0.25rem] shrink-0 size-1 ${
                  index === activeIndex
                    ? 'bg-gradient-to-b from-[#fff0d0] to-[#ffb352] via-50% via-[#ffaa00]'
                    : 'bg-[rgba(255,255,255,0.35)]'
                }`}
              />
            ))}
          </div>

          <div
            className="flex items-center justify-center relative shrink-0 cursor-pointer"
            onClick={handleNext}>
            <div className="flex-none rotate-180 scale-y-[-100%]">
              <div className="relative size-4">
                <img
                  alt=""
                  className="block max-w-none size-full"
                  src={carouselLeftImage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Friends 按钮 */}
      <div
        className="border border-[rgba(255,255,255,0.2)] border-solid box-border flex gap-1 items-center justify-center px-6 py-3 relative rounded-[0.5rem] w-full cursor-pointer"
        style={{
          backgroundImage:
            'linear-gradient(rgb(240, 222, 130) 0%, rgba(180, 125, 45, 0.824) 98%), linear-gradient(rgb(214, 170, 83) 12.166%, rgb(124, 68, 26) 100%)',
        }}
        onClick={() => doShare()}>
        <p
          className="font-medium text-base text-center text-nowrap uppercase whitespace-pre"
          style={{color: '#51320b'}}>
          {i18n.t('agent.myTeam.inviteFriends')}
        </p>
      </div>
    </div>
  );
};

export default UserDataSection;
